import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { TableColumn } from "../../components/common/Table/Table";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import { handleApiError } from "../../pages/UBTAdmin/ProjectDetails/ProjectHelper/ViewProjectHandleApiError";
import { DropdownOption } from "../../components/common/Dropdown";

export const searchByOptions = [
  { id: 0, label: "UBT Name", value: "UBTName" },
  { id: 1, label: "Cost Center Name", value: "CostCenterName" },
  { id: 2, label: "Cost Center Number", value: "CostCenterNumber" },
];

export const teamsInfoColumn: TableColumn[] = [
  { key: "deleteIcon", label: "" },
  { key: "id", label: "ID" },
  { key: "ubtName", label: "Name of UBT", className: "md:max-w-[7rem]" },
  { key: "serviceType", label: "Service type", className: "md:max-w-[8rem]" },
  { key: "usingHuddles", label: "Using huddles" },
  {
    key: "dateTeamEstablished",
    label: "Date team established",
    className: "md:max-w-[8rem]",
  },
  { key: "dateTeamClosed", label: "Date team closed" },
  { key: "teamRating", label: "Team rating" },
];

export const projectListColumn: TableColumn[] = [
  { key: "deleteIcon", label: "" },
  { key: "status", label: "Status", isSortable: true },
  { key: "lastUpdated", label: "Last updated", className: "md:max-w-[9rem]", isSortable: true },
  { key: "startDate", label: "Start date", className: "md:max-w-[8rem]", isSortable: true },
  { key: "nameOfProject", label: "Name of project" },
  { key: "focusArea", label: "Focus area" },
  {
    key: "performanceMetric",
    label: "Performance metric",
    className: "md:max-w-[10rem]",
  },
  { key: "viewDetails", label: "View details" },
];

const teamReportOptions: DropdownOption[] = [
  {
    id: "teams_summary",
    label: "Teams Summary",
    value: "teams_summary",
    iconPath: "/assets/images/printer-outline.svg",
  },
  {
    id: "teams_details",
    label: "Teams Details",
    value: "teams_details",
    iconPath: "/assets/images/printer-outline.svg",
  },
  {
    id: "export_pip_history_csv",
    label: "Export PIP History 	 (CSV)",
    value: "export_pip_history_csv",
    iconPath: "/assets/images/printer-outline.svg",
  },
  {
    id: "export_pip_detail_csv",
    label: "Export PIP Details 	 (CSV)",
    value: "export_pip_detail_csv",
    iconPath: "/assets/images/printer-outline.svg",
  },
  {
    id: "export_test_of_change_csv",
    label: "Export Test of Change 	 (CSV)",
    value: "export_test_of_change_csv",
    iconPath: "/assets/images/printer-outline.svg",
  },
];

const projectReportOptions: DropdownOption[] = [
  {
    id: "test_data_charts",
    label: "Test Data Charts",
    value: "test_data_charts",
    iconPath: "/assets/images/printer-outline.svg",
  },
   {
    id: "print_project_record",
    label: "Print Project Record",
    value: "print_project_record",
    iconPath: "/assets/images/printer-outline.svg",
  },
   {
    id: "print_test_of_change",
    label: "Print Test of Change",
    value: "print_test_of_change",
    iconPath: "/assets/images/printer-outline.svg",
  },
  {
    id: "export_test_data",
    label: "Export Test Data 	 (CSV)",
    value: "export_test_data",
    iconPath: "/assets/images/printer-outline.svg",
  },
];


export interface ProjectDetailsState {
  projectDetails: { [key: string]: any } | null;
  projectDetailsLoading: boolean;
  projectDetailsError: string | null;
  projectAccess: { canEdit: boolean; canDelete: boolean } | null;
  projectAccessLoading: boolean;
  projectAccessError: string | null;
  isTeamTracked: boolean;
  isTeamTrackedLoading: boolean;
  isTeamTrackedError: string | null;
  addTeamToMyTeamsLoading: boolean;
  addTeamToMyTeamsError: string | null;
  addTeamToMyTeamsSuccess: boolean;
  teamReportOptions: DropdownOption[];
  projectReportOptions: DropdownOption[];
  selectedProjectIndex: number;
}

const initialState: ProjectDetailsState = {
  projectDetails: null,
  projectDetailsLoading: false,
  projectDetailsError: null,
  projectAccess: null,
  projectAccessLoading: false,
  projectAccessError: null,
  isTeamTracked: false,
  isTeamTrackedLoading: false,
  isTeamTrackedError: null,
  addTeamToMyTeamsLoading: false,
  addTeamToMyTeamsError: null,
  addTeamToMyTeamsSuccess: false,
  selectedProjectIndex: 0,
  teamReportOptions: teamReportOptions,
  projectReportOptions: projectReportOptions,
};



export const fetchProjectDetails = createAsyncThunk(
  "ubtPortal/fetchProjectDetails",
  async (
    {
      projectid,
      nuid,
    }: { projectid: number; fields?: string[] | string; nuid: string | null },
    { rejectWithValue, signal },
  ) => {
    try {
      const params: any = {};

      const options: any = {
        signal,
        params,
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url =
        typeof endpoints.ubt.projectDetails.getpipDetails === "function"
          ? endpoints.ubt.projectDetails.getpipDetails(projectid)
          : endpoints.ubt.projectDetails.getpipDetails;
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(error, rejectWithValue, "Failed to fetch project details");
    }
  },
);

export const deleteProject = createAsyncThunk(
  'ubtPortal/deleteProject',
  async (
    { projectid, nuid }: { projectid: number; nuid: string | null },
    { rejectWithValue },
  ) => {
    try {
      const options: any = {
        headers: {
          accept: 'application/json',
          ...(nuid ? { 'x-nuid': nuid } : {}),
        },
      };

      const url =
        typeof endpoints.ubt.projectDetails.deletepip === 'function'
          ? endpoints.ubt.projectDetails.deletepip(projectid)
          : endpoints.ubt.projectDetails.deletepip;

      const response = await api.delete(url, options);
      const data = response.data;

      if (data?.success === false || data?.isSuccess === false) {
        const message = data?.message ?? 'Failed to delete project';
        if (typeof message === 'string' && message.includes('ORA-02292')) {
          return rejectWithValue(
            'In order to delete this project, you must first delete all team documents associated with it.'
          );
        }
        return rejectWithValue(message);
      }

      return data;

    } catch (error: any) {
      return handleApiError(error, rejectWithValue, 'Failed to delete project');
    }
  },
);

/**
 * Fetches project access permissions for a team.
 * @param teamId - Team identifier used by the project access endpoint.
 * @param nuid - Current user NUID used for x-nuid request header.
 * @returns API response containing access flags (for example canEdit/canDelete).
 */
export const fetchProjectAccess = createAsyncThunk(
  'ubtPortal/fetchProjectAccess',
  async (
    { teamId, nuid }: { teamId: number | string; nuid: string | null },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: 'application/json',
          ...(nuid ? { 'x-nuid': nuid } : {}),
        },
      };

      const url =
        typeof endpoints.ubt.projectDetails.getProjectAccess === 'function'
          ? endpoints.ubt.projectDetails.getProjectAccess(teamId)
          : endpoints.ubt.projectDetails.getProjectAccess;

      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(error, rejectWithValue, 'Failed to fetch project access');
    }
  },
);

/**
 * Fetches team tracked status used to control Add to my teams visibility.
 * @param teamId - Team identifier used by the is-tracked endpoint.
 * @param nuid - Current user NUID used for x-nuid request header.
 * @returns API response containing `isTracked` boolean.
 */
export const fetchTeamTrackedStatus = createAsyncThunk(
  'ubtPortal/fetchTeamTrackedStatus',
  async (
    { teamId, nuid }: { teamId: number | string; nuid: string | null },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: 'application/json',
          ...(nuid ? { 'x-nuid': nuid } : {}),
        },
      };

      const url =
        typeof endpoints.ubt.projectDetails.getTeamTrackedStatus === 'function'
          ? endpoints.ubt.projectDetails.getTeamTrackedStatus(teamId)
          : endpoints.ubt.projectDetails.getTeamTrackedStatus;

      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(error, rejectWithValue, 'Failed to fetch team tracked status');
    }
  },
);

// Delete a team from the Project Details page (accepts number or { id, nuid })
export const deleteMyTeam = createAsyncThunk(
  'ubtPortal/deleteMyTeam',
  async (
    arg: number | { id: number; nuid?: string | null },
    { rejectWithValue },
  ) => {
    try {
      const id = typeof arg === 'number' ? arg : arg?.id;
      const nuid = typeof arg === 'number' ? null : arg?.nuid ?? null;
      if (typeof id !== 'number') {
        return rejectWithValue('Invalid team id');
      }

      const options: any = {
        headers: {
          accept: 'application/json',
          ...(nuid ? { 'x-nuid': nuid } : {}),
        },
      };

      const url =
        typeof endpoints.ubt.projectDetails.deleteProjectTeam === 'function'
          ? endpoints.ubt.projectDetails.deleteProjectTeam(id)
          : endpoints.ubt.projectDetails.deleteProjectTeam;

      const response = await api.delete(url, options);
      return response.data ?? id;
    } catch (error: any) {
      return handleApiError(error, rejectWithValue, 'Failed to delete team');
    }
  },
);

/**
 * Add a team to "My Teams" list via POST request
 * @param payload - { teamId: number, nuid: string | null }
 * @returns Response data from API
 */
export const addTeamToMyTeams = createAsyncThunk(
  'ubtPortal/addTeamToMyTeams',
  async (
    { teamId, nuid }: { teamId: number | string; nuid: string | null },
    { rejectWithValue },
  ) => {
    try {
      const options: any = {
        headers: {
          'content-type': 'application/json',
          ...(nuid ? { 'x-nuid': nuid } : {}),
        },
      };

      const url = endpoints.ubt.projectDetails.addTeamToMyTeams;
      const response = await api.post(url, { teamId }, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(error, rejectWithValue, 'Failed to add team to my teams');
    }
  },
);

const pipDetailsSlice = createSlice({
  name: "pipDetails",
  initialState,
  reducers: {
    resetProjectDetails: (state) => {
      state.projectDetails = null;
      state.projectDetailsLoading = false;
      state.projectDetailsError = null;
      state.projectAccess = null;
      state.projectAccessLoading = false;
      state.projectAccessError = null;
      // isTeamTracked and addTeamToMyTeams* are team-scoped, not project-scoped.
      // They must not be reset here to avoid wiping a valid tracked status that
      // was fetched for the current team before the project list updates.
    },
    setSelectedProjectIndex(state, action) {
      state.selectedProjectIndex = action.payload;
    },
    setProjectDetails: (state, action: PayloadAction<any>) => {
      state.projectDetails = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProjectDetails.pending, (state) => {
      state.projectDetailsLoading = true;
      state.projectDetailsError = null;
    });

    builder.addCase(fetchProjectDetails.fulfilled, (state, action) => {
      state.projectDetailsLoading = false;
      state.projectDetails = action.payload ?? null;
    });

    builder.addCase(fetchProjectDetails.rejected, (state, action) => {
      state.projectDetailsLoading = false;
      state.projectDetailsError = action.payload as string;
    });

    builder.addCase(fetchProjectAccess.pending, (state) => {
      state.projectAccessLoading = true;
      state.projectAccessError = null;
    });

    builder.addCase(fetchProjectAccess.fulfilled, (state, action) => {
      state.projectAccessLoading = false;
      state.projectAccess = action.payload ?? null;
    });

    builder.addCase(fetchProjectAccess.rejected, (state, action) => {
      state.projectAccessLoading = false;
      state.projectAccessError = action.payload as string;
      state.projectAccess = null;
    });

    builder.addCase(fetchTeamTrackedStatus.pending, (state) => {
      state.isTeamTrackedLoading = true;
      state.isTeamTrackedError = null;
    });

    builder.addCase(fetchTeamTrackedStatus.fulfilled, (state, action) => {
      const payload: any = action.payload;
      state.isTeamTrackedLoading = false;
      state.isTeamTracked = Boolean(
        payload?.isTracked ?? payload?.data?.isTracked ?? payload?.tracked ?? false,
      );
    });

    builder.addCase(fetchTeamTrackedStatus.rejected, (state, action) => {
      state.isTeamTrackedLoading = false;
      state.isTeamTrackedError = action.payload as string;
      state.isTeamTracked = false;
    });

    builder.addCase(addTeamToMyTeams.pending, (state) => {
      state.addTeamToMyTeamsLoading = true;
      state.addTeamToMyTeamsError = null;
      state.addTeamToMyTeamsSuccess = false;
    });

    builder.addCase(addTeamToMyTeams.fulfilled, (state) => {
      state.addTeamToMyTeamsLoading = false;
      state.addTeamToMyTeamsSuccess = true;
      state.addTeamToMyTeamsError = null;
    });

    builder.addCase(addTeamToMyTeams.rejected, (state, action) => {
      state.addTeamToMyTeamsLoading = false;
      state.addTeamToMyTeamsError = action.payload as string;
      state.addTeamToMyTeamsSuccess = false;
    });
  },
});

export const { resetProjectDetails, setProjectDetails, setSelectedProjectIndex } = pipDetailsSlice.actions;
export default pipDetailsSlice.reducer;
